import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ProposalId } from '@shared/models/proposals.model';
import { useProjectStateStore } from '@client/store/project/project.store';
import { getBuildingVersionById } from '@shared/helpers/recursive-element.helpers';

/**
 * Search-param key used to keep the currently selected proposal in the URL.
 * Missing means the version's first proposal; `baseline` is the explicit
 * baseline marker.
 *
 * Reset on scope change (project / version) is handled centrally in
 * `useUrlToUiStoreSync`.
 */
export const PROPOSAL_SEARCH_PARAM = 'proposal';
export const BASELINE_PROPOSAL_SEARCH_VALUE = 'baseline';

export const proposalIdFromSearchValue = (
  value: string | null | undefined,
): ProposalId | undefined =>
  value && value !== BASELINE_PROPOSAL_SEARCH_VALUE ? value : undefined;

export const isBaselineProposalSearchValue = (
  value: string | null | undefined,
): boolean => value === BASELINE_PROPOSAL_SEARCH_VALUE;

/**
 * Resolves selection against the version that is actually rendered. During a
 * version route change the previous proposal id can remain in the URL for one
 * render; treating that foreign id as selected briefly mounts baseline. Keep
 * explicit ids while the version is still loading, but fall back to the first proposal
 * as soon as the destination version is available.
 */
export const resolveSelectedProposalIdForVersion = (
  value: string | null | undefined,
  versionProposalIds: readonly ProposalId[] | undefined,
): ProposalId | undefined => {
  if (isBaselineProposalSearchValue(value)) return undefined;
  const proposalId = proposalIdFromSearchValue(value);
  if (
    proposalId &&
    (versionProposalIds === undefined ||
      versionProposalIds.includes(proposalId))
  ) {
    return proposalId;
  }
  return versionProposalIds?.[0];
};

/**
 * Returns the currently selected {@link IProposal} id. An explicit baseline
 * marker returns `undefined`; a missing query value defaults synchronously to
 * the version's first proposal so baseline never flashes while opening a
 * project.
 *
 * Lives in the URL (instead of the UI store) so that it is shareable via link
 * and survives reloads.
 */
export const useSelectedProposalId = (): ProposalId | undefined => {
  const [searchParams] = useSearchParams();
  const { versionId } = useParams<{ versionId: string }>();
  const version = useProjectStateStore((state) =>
    getBuildingVersionById(state.project, versionId, false),
  );
  const versionProposalIds = useMemo(
    () => version?.proposals?.map(({ id }) => id),
    [version?.proposals],
  );
  const value = searchParams.get(PROPOSAL_SEARCH_PARAM);
  return resolveSelectedProposalIdForVersion(value, versionProposalIds);
};

/**
 * Returns a setter that writes the selected {@link IProposal} id to the
 * `?proposal=` search param. Pass `undefined` (or an empty string) to select
 * baseline explicitly. A marker is retained so a missing query can remain the
 * unambiguous default for the first proposal.
 *
 * Uses `replace: true` so toggling selection does not pollute browser history.
 */
export const useSetSelectedProposalId = (): ((id?: ProposalId) => void) => {
  const [, setSearchParams] = useSearchParams();

  return useCallback(
    (id) => {
      setSearchParams(
        (params) => {
          const current = params.get(PROPOSAL_SEARCH_PARAM) ?? undefined;
          const next = id || BASELINE_PROPOSAL_SEARCH_VALUE;
          if (current === next) return params;
          params.set(PROPOSAL_SEARCH_PARAM, next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
};

/** Raw proposal URL value, preserving the explicit baseline marker. */
export const getSelectedProposalSearchValueFromUrl = ():
  ProposalId | typeof BASELINE_PROPOSAL_SEARCH_VALUE | undefined =>
  new URLSearchParams(window.location.search).get(PROPOSAL_SEARCH_PARAM) ??
  undefined;

/**
 * Reads the selected proposal id directly from `window.location` without
 * subscribing to React Router. Use only from non-component contexts (e.g.
 * store subscribers) where calling hooks is not possible.
 */
export const getSelectedProposalIdFromUrl = (): ProposalId | undefined => {
  return proposalIdFromSearchValue(getSelectedProposalSearchValueFromUrl());
};
